import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Shield, CheckCircle2, AlertCircle } from 'lucide-react';

/**
 * Admin reset-password page.
 * Reached via the email link: /admin/reset-password?token=<token>
 */
const AdminResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [tokenValid, setTokenValid] = useState(null); // null = checking, true/false
  const [tokenReason, setTokenReason] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setTokenValid(false);
        setTokenReason('missing_token');
        return;
      }
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/reset-password/validate?token=${encodeURIComponent(
            token
          )}`
        );
        const data = await res.json();
        setTokenValid(Boolean(data.valid));
        if (!data.valid) setTokenReason(data.reason || 'invalid');
      } catch (e) {
        setTokenValid(false);
        setTokenReason('network');
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 12) {
      setError('New password must be at least 12 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/reset-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, new_password: newPassword }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Could not reset password. The link may be invalid.');
        return;
      }
      setDone(true);
    } catch (err) {
      console.error('Reset error:', err);
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const reasonCopy = {
    missing_token: 'This reset link is missing its token. Please request a new one.',
    not_found: 'This reset link is not recognised.',
    used: 'This reset link has already been used.',
    expired: 'This reset link has expired. Please request a new one.',
    invalid: 'This reset link is invalid.',
    network: 'We could not verify the reset link. Please try again.',
  };

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink font-body flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-brand-surface border border-brand-border shadow-[0_8px_30px_rgb(0,0,0,0.06)] overflow-hidden">
          <div className="bg-brand-ink text-white p-8 text-center">
            <div className="border border-white/20 p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <Shield className="h-10 w-10" />
            </div>
            <h1 className="font-display text-3xl">Set a new password</h1>
            <p className="text-xs uppercase tracking-[0.25em] text-brand-sage mt-2">ReflectiveMind Administration</p>
          </div>

          <div className="p-8">
            {tokenValid === null && (
              <p className="text-sm text-brand-ink-muted text-center" data-testid="reset-checking">
                Verifying your reset link…
              </p>
            )}

            {tokenValid === false && (
              <div className="text-center" data-testid="reset-invalid">
                <div className="inline-flex items-center justify-center bg-red-50 border border-red-200 p-3 mb-4">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
                <p className="text-sm text-brand-ink-muted mb-6">
                  {reasonCopy[tokenReason] || 'This reset link is invalid.'}
                </p>
                <button
                  onClick={() => navigate('/admin/login')}
                  className="text-xs uppercase tracking-[0.2em] text-brand-accent hover:text-brand-cta transition-colors"
                  data-testid="reset-back-to-login"
                >
                  ← Back to login
                </button>
              </div>
            )}

            {tokenValid === true && !done && (
              <form onSubmit={handleSubmit} data-testid="reset-form">
                <p className="text-sm text-brand-ink-muted mb-6">
                  Choose a strong new admin password (at least 12 characters).
                </p>

                {error && (
                  <div className="mb-5 p-3 bg-red-50 border border-red-200 flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <div className="space-y-5">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-brand-sage mb-2">
                      New password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-0 top-1/2 transform -translate-y-1/2 h-4 w-4 text-brand-ink-muted" />
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        className="w-full pl-7 pr-4 py-2.5 bg-transparent border-0 border-b border-brand-border focus:border-brand-accent outline-none rounded-none text-brand-ink placeholder:text-brand-ink-muted/50"
                        placeholder="At least 12 characters"
                        data-testid="reset-new-password"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-brand-sage mb-2">
                      Confirm new password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-0 top-1/2 transform -translate-y-1/2 h-4 w-4 text-brand-ink-muted" />
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className="w-full pl-7 pr-4 py-2.5 bg-transparent border-0 border-b border-brand-border focus:border-brand-accent outline-none rounded-none text-brand-ink placeholder:text-brand-ink-muted/50"
                        placeholder="Repeat new password"
                        data-testid="reset-confirm-password"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-brand-cta hover:bg-brand-cta-hover text-white py-3 px-6 font-medium transition-all shadow-[0_4px_14px_rgba(5,112,222,0.35)] hover:-translate-y-0.5 hover:shadow-[0_2px_8px_rgba(5,112,222,0.25)] disabled:opacity-50 disabled:cursor-not-allowed "
                    data-testid="reset-submit"
                  >
                    {submitting ? 'Updating…' : 'Set new password'}
                  </button>
                </div>
              </form>
            )}

            {done && (
              <div className="text-center" data-testid="reset-success">
                <div className="inline-flex items-center justify-center bg-brand-sage/15 border border-brand-sage/30 p-3 mb-5">
                  <CheckCircle2 className="h-8 w-8 text-brand-sage" />
                </div>
                <h2 className="font-display text-2xl text-brand-ink mb-3">Password updated</h2>
                <p className="text-sm text-brand-ink-muted mb-6">
                  You can now sign in with your new password.
                </p>
                <button
                  onClick={() => navigate('/admin/login')}
                  className="bg-brand-cta hover:bg-brand-cta-hover text-white py-2.5 px-6 font-medium transition-all shadow-[0_4px_14px_rgba(5,112,222,0.35)] hover:-translate-y-0.5 hover:shadow-[0_2px_8px_rgba(5,112,222,0.25)]"
                  data-testid="reset-go-to-login"
                >
                  Go to login
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminResetPassword;
